import { supabase } from '../lib/supabase';
import { AiAgentLog } from '../types';

// Camada de serviço do atendente de IA no chat: estado por lead (ligado/desligado), última
// sugestão pendente de revisão e o registro do que o vendedor fez com ela. Quem grava as
// sugestões é o gateway (edge function); aqui o usuário só lê e revisa.

// Atendente de IA em piloto: só a Evolua Negócios (empresa 29) e só o CEO veem e usam. O banco confere de novo
// em ai_enroll_leads; o resto da tela some para os demais.
export const AI_AGENT_COMPANY_ID = 29;
// Gestores liberados a administrar a IA da própria empresa (espelha a tabela ai_agent_admins; o banco confere de novo).
// FDM Portas (empresa 23): Gestor FDM.
const AI_AGENT_COMPANY_ADMINS: Record<string, number> = { '941137e0-fe45-4126-b9ac-2caa4f6aa672': 23 };
export const canUseAiAgent = (role: string | null | undefined, companyId: number | null | undefined, userId?: string | null) =>
  (companyId === AI_AGENT_COMPANY_ID && (role || '').toLowerCase().trim() === 'ceo')
  || (!!userId && companyId != null && AI_AGENT_COMPANY_ADMINS[userId] === companyId);

export interface AiLeadState {
  enabled: boolean;
  mode: 'copilot' | 'auto';
  /** Enquanto estiver no futuro, a IA não responde este lead (humano assumiu ou a IA passou o lead). */
  pausedUntil: string | null;
}

const SUGGESTION_COLUMNS = 'id, company_id, lead_id, kind, stage, decision, message, reason, mode, status, final_message, created_at';

export async function fetchAiLeadState(leadId: number): Promise<AiLeadState | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from('leads').select('ai_enabled, ai_mode, ai_paused_until').eq('id', leadId).maybeSingle();
  if (error || !data) return null;
  return { enabled: !!data.ai_enabled, mode: data.ai_mode === 'copilot' ? 'copilot' : 'auto', pausedUntil: data.ai_paused_until ?? null };
}

export async function setAiEnabled(leadId: number, enabled: boolean): Promise<void> {
  if (!supabase) return;
  // Ao ligar, a IA atende sozinha (modo automático) e pausa/trava antigas são limpas.
  const patch = enabled
    ? { ai_enabled: true, ai_mode: 'auto', ai_paused_until: null, ai_lock_until: null }
    : { ai_enabled: false };
  const { error } = await supabase.from('leads').update(patch).eq('id', leadId);
  if (error) throw error;
}

// Direciona leads para a IA em modo SDR: ela liga, faz o primeiro contato (em ritmo limitado), qualifica e agenda reunião.
// Só a gestão pode (checado no banco). Devolve quantos leads foram direcionados.
export async function enrollLeadsForReactivation(leadIds: number[]): Promise<number> {
  if (!supabase || leadIds.length === 0) return 0;
  const { data, error } = await supabase.rpc('ai_enroll_leads', { p_lead_ids: leadIds, p_start: true });
  if (error) throw error;
  return Number(data) || 0;
}

// O atendente humano devolve o lead para a IA (depois de ela pausar ou de um humano assumir).
export async function reactivateAi(leadId: number): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').update({ ai_paused_until: null, ai_lock_until: null }).eq('id', leadId);
  if (error) throw error;
}

// Última passagem para humano feita pela IA (motivo e resumo), para mostrar enquanto o lead está pausado.
export async function fetchLatestHandoff(leadId: number): Promise<AiAgentLog | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('ai_agent_log')
    .select(SUGGESTION_COLUMNS)
    .eq('lead_id', leadId)
    .eq('decision', 'handoff')
    .eq('status', 'sent')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data as AiAgentLog) ?? null;
}

// Última sugestão ainda não revisada deste lead (respostas e recomendações de passar para humano).
export async function fetchLatestSuggestion(leadId: number): Promise<AiAgentLog | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('ai_agent_log')
    .select(SUGGESTION_COLUMNS)
    .eq('lead_id', leadId)
    .eq('status', 'suggested')
    .in('decision', ['reply', 'handoff', 'opt_out'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data as AiAgentLog) ?? null;
}

// Registra o que o vendedor fez: 'sent' (enviou como estava), 'edited' (enviou editada) ou 'rejected'.
export async function reviewSuggestion(
  id: string,
  status: 'sent' | 'edited' | 'rejected',
  finalMessage: string | null,
  userId: string,
): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('ai_agent_log')
    .update({ status, final_message: finalMessage, reviewed_by: userId, reviewed_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export function subscribeToAiSuggestions(leadId: number, onChange: () => void): () => void {
  if (!supabase) return () => {};
  const client = supabase;
  const channel = client
    .channel(`ai_agent_log_${leadId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_agent_log', filter: `lead_id=eq.${leadId}` }, () => onChange())
    .subscribe();
  return () => { client.removeChannel(channel); };
}

// ---- Página de configuração e ativação (somente o CEO da Evolua; o banco confere em ai_admin_*) ----
export interface AiAdminOverview {
  /** Empresa administrada. `owner` = CEO da Evolua (chaves, interruptor geral e config global); os demais só mexem no agente da empresa. */
  scope?: { company_id: number; owner: boolean; agent_id: string | null; agent_name: string | null; agent_enabled: boolean };
  settings: Record<string, string>;
  keys: { anthropic: boolean; stt: boolean };
  stats: {
    active_leads: number; waiting_first_touch: number; paused_leads: number; first_touches_24h: number;
    replies_24h: number; handoffs_7d: number; meetings_upcoming: number; cost_7d_usd: number;
  };
  users: { id: string; name: string; role: string }[];
  instances: { id: string; name: string; status: string }[];
  stages: string[];
  leads: {
    id: number; name: string; phone: string | null; followups: number; last_at: string | null; meeting_at: string | null;
    state: 'desligada' | 'aguardando_primeiro_contato' | 'com_humano' | 'pausada' | 'ativa';
  }[];
  activity: { at: string; lead: string; lead_id: number; decision: string; status: string; reason: string | null; message: string | null }[];
}

export async function fetchAiAdminOverview(): Promise<AiAdminOverview> {
  if (!supabase) throw new Error('Sem conexão com o banco');
  const { data, error } = await supabase.rpc('ai_admin_overview');
  if (error) throw error;
  return data as AiAdminOverview;
}

export async function saveAiAdminSettings(values: Record<string, string>): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.rpc('ai_admin_save_settings', { p: values });
  if (error) throw error;
}

export async function aiAdminLeadAction(leadId: number, action: 'disable' | 'resume'): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.rpc('ai_admin_lead_action', { p_lead_id: leadId, p_action: action });
  if (error) throw error;
}

export interface AiPendingOutcome {
  event_id: string; lead_id: number; lead: string; phone: string | null; start_time: string; host: string | null; ai: boolean;
}

// Reuniões dos últimos 30 dias que já passaram e ainda não tiveram o resultado marcado na Agenda.
export async function fetchAiPendingOutcomes(): Promise<AiPendingOutcome[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('ai_admin_pending_outcomes');
  if (error) throw error;
  return (data as AiPendingOutcome[]) ?? [];
}

// ---- Materiais que a Stella pode enviar (fotos, vídeos, PDFs, links e textos prontos) ----
export type AiMaterialMediaType = 'image' | 'video' | 'audio' | 'document';
export interface AiMaterial {
  id: string; slug: string; title: string; when_to_use: string; kind: 'media' | 'text';
  media_path: string | null; media_type: AiMaterialMediaType | null; media_name: string | null; message: string | null;
  enabled: boolean; sort: number; sends: number;
}
export interface AiMaterialDraft {
  id?: string; title: string; when_to_use: string; kind: 'media' | 'text';
  media_path: string | null; media_type: AiMaterialMediaType | null; media_name: string | null; message: string; enabled: boolean;
}

const AI_MATERIAL_BUCKET = 'whatsapp_media';
export const AI_MATERIAL_MAX_BYTES = 15 * 1024 * 1024;

export async function fetchAiMaterials(): Promise<AiMaterial[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('ai_admin_materials');
  if (error) throw error;
  return (data as AiMaterial[]) ?? [];
}

export async function saveAiMaterial(d: AiMaterialDraft): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.rpc('ai_admin_material_save', { p: d });
  if (error) throw error;
}

export async function archiveAiMaterial(id: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.rpc('ai_admin_material_archive', { p_id: id });
  if (error) throw error;
}

export function detectMaterialType(mime: string): AiMaterialMediaType {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

// Guarda o arquivo no mesmo bucket das mídias do WhatsApp; a fila de envio lê dali.
export async function uploadAiMaterialFile(file: File): Promise<{ path: string; type: AiMaterialMediaType; name: string }> {
  if (!supabase) throw new Error('Sem conexão com o banco');
  if (file.size > AI_MATERIAL_MAX_BYTES) throw new Error('Arquivo muito grande. O limite é 15 MB.');
  const safe = file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-80);
  const path = `ai-materials/${Date.now()}-${safe}`;
  const { error } = await supabase.storage.from(AI_MATERIAL_BUCKET).upload(path, file, { contentType: file.type || undefined, upsert: false });
  if (error) throw error;
  return { path, type: detectMaterialType(file.type), name: file.name };
}

// ---- WhatsApp (instância) usado pela Stella ----
export interface AiInstance { id: string; name: string; instance_name: string; status: string; owner: string | null; created_at: string; in_use: boolean }

export async function fetchAiInstances(): Promise<AiInstance[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('ai_admin_instances');
  if (error) throw error;
  return (data as AiInstance[]) ?? [];
}
